import React from 'react';
import { TextStyle, Text } from '@cad-arc';
import { partition } from 'lodash';
import {
    DispatchAreaGroupObject,
    DispatchAreaObject,
    DispatchAreaUserTypeEnumType,
} from '@mark43/cad-api';

import { UseFormReturn } from 'react-hook-form';
import { DispatchAreaManagementForm } from './types';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

export function DispatchFor(props: {
    form: UseFormReturn<DispatchAreaManagementForm>;
    allDispatchAreaObjects: DispatchAreaObject[];
    allDispatchAreaGroupObjects: DispatchAreaGroupObject[];
}): React.ReactElement {
    const { ownDispatchAreas } = useEnabledFeatures();
    const allDispatchAreaIdsInGroups = React.useMemo(() => {
        const obj: { [id: string]: number } = {};
        for (const dispatchAreaGroupObject of props.allDispatchAreaGroupObjects) {
            for (const dispatchAreaObject of dispatchAreaGroupObject.dispatchAreaObjects) {
                obj[dispatchAreaObject.id] = dispatchAreaGroupObject.id;
            }
        }
        return obj;
    }, [props.allDispatchAreaGroupObjects]);
    const allDispatchAreaGroupObjectsById = React.useMemo(() => {
        return Object.fromEntries(
            props.allDispatchAreaGroupObjects.map((dispatchAreaGroupObject) => [
                dispatchAreaGroupObject.id,
                dispatchAreaGroupObject,
            ])
        );
    }, [props.allDispatchAreaGroupObjects]);
    const allDispatchAreaObjectsByDispatchAreaId = React.useMemo(() => {
        return Object.fromEntries(
            props.allDispatchAreaObjects.map((dispatchAreaObject) => [
                dispatchAreaObject.id,
                dispatchAreaObject,
            ])
        );
    }, [props.allDispatchAreaObjects]);
    const allValues = props.form.watch();
    const dispatchForMessage = React.useMemo(() => {
        const messageSegments: string[] = [];
        const ownedValues: DispatchAreaManagementForm = {};
        for (const [key, value] of Object.entries(allValues)) {
            const statusToTrack: DispatchAreaUserTypeEnumType = ownDispatchAreas
                ? 'OWNER'
                : 'MONITOR';
            if (value === statusToTrack) {
                ownedValues[key] = value;
            }
        }
        const [entriesInGroups, entriesNotInGroups] = partition(
            Object.entries(ownedValues),
            (entry) => {
                return !!allDispatchAreaIdsInGroups[entry[0]];
            }
        );
        const ownedDispatchAreasByGroups: { [groupId: number]: string[] } = {};
        for (const [key] of entriesInGroups) {
            if (allDispatchAreaIdsInGroups[key]) {
                if (!ownedDispatchAreasByGroups[allDispatchAreaIdsInGroups[key]]) {
                    ownedDispatchAreasByGroups[allDispatchAreaIdsInGroups[key]] = [];
                }
                ownedDispatchAreasByGroups[allDispatchAreaIdsInGroups[key]].push(key);
            }
        }
        for (const groupId in ownedDispatchAreasByGroups) {
            messageSegments.push(
                `${allDispatchAreaGroupObjectsById[groupId].name} (${ownedDispatchAreasByGroups[
                    groupId
                ]
                    .map(
                        (dispatchAreaObjectId) =>
                            allDispatchAreaObjectsByDispatchAreaId[dispatchAreaObjectId].name
                    )
                    .join(', ')})`
            );
        }
        for (const dispatchAreaEntry of entriesNotInGroups) {
            messageSegments.push(
                `${allDispatchAreaObjectsByDispatchAreaId[dispatchAreaEntry[0]].name}`
            );
        }
        return messageSegments.join(', ');
    }, [
        allValues,
        ownDispatchAreas,
        allDispatchAreaIdsInGroups,
        allDispatchAreaGroupObjectsById,
        allDispatchAreaObjectsByDispatchAreaId,
    ]);

    return (
        <TextStyle fontWeight="normal">
            <Text variant="bodyMd" data-testid="dispatch-for">
                {ownDispatchAreas ? 'Dispatch For' : 'Monitor'}:{' '}
                {dispatchForMessage ? dispatchForMessage : '--'}
            </Text>
        </TextStyle>
    );
}
